import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { formatViews, getCategoryName } from "@/lib/content";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "horizontal" | "compact";
}

export default function ArticleCard({
  article,
  variant = "default",
}: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-3 py-3 cursor-pointer"
      >
        <div className="w-20 h-20 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 51吃瓜`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="min-w-0 flex-1">
          <span className="text-xs text-primary-600">{getCategoryName(article.category)}</span>
          <h3 className="mt-1 text-sm leading-snug text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="mt-1.5 text-xs text-foreground-400">
            {article.publishedAt} · {formatViews(article.views)} 阅读
          </p>
        </div>
      </Link>
    );
  }

  if (variant === "horizontal") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-full sm:w-44 h-40 sm:h-32 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 51吃瓜`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="min-w-0 flex-1 flex flex-col">
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800">
              {getCategoryName(article.category)}
            </span>
            <span>{article.publishedAt}</span>
          </div>
          <h3 className="mt-2 font-heading font-bold text-base md:text-lg text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
            {article.excerpt}
          </p>
          <div className="mt-auto pt-3 flex items-center gap-4 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(article.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readMinutes} 分钟
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <article className="group flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors">
      <Link to={`/article/${article.id}`} className="cursor-pointer">
        <div className="w-full aspect-[4/3] overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 51吃瓜`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      </Link>
      <div className="flex flex-col flex-1 p-4 md:p-5">
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-800">
            {getCategoryName(article.category)}
          </span>
          <span className="text-foreground-400">{article.publishedAt}</span>
          {article.trending && (
            <span className="ml-auto flex items-center gap-1 text-primary-600">
              <i className="ri-fire-line"></i>
              热
            </span>
          )}
        </div>
        <h3 className="mt-3 font-heading font-bold text-base md:text-lg text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
          <Link to={`/article/${article.id}`}>{article.title}</Link>
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
          {article.excerpt}
        </p>
        <div className="mt-auto pt-4 flex items-center justify-between text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-user-3-line"></i>
            {article.author}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </article>
  );
}